import React from 'react';
import { motion } from 'framer-motion';
import { Zap, Target } from 'lucide-react';

const WhaleIcon: React.FC<{ size?: number }> = ({ size = 32 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 9.1V5a2 2 0 0 0-4 0"/>
    <path d="M18 5a2 2 0 0 1 4 0"/>
    <path d="M6 9.7 3.9 8.4C2.7 7.7 2 6.4 2 5V3c2 0 4 2 4 2s2-2 4-2v2c0 1.4-.7 2.7-1.9 3.4l-3.8 2.4A5 5 0 0 0 7 20h12c1.7 0 3-1.3 3-3v-3c0-2.8-2.2-5-5-5-2.7 0-5.1 1.4-6.4 3.6L9.7 14A2 2 0 0 1 6 13Z"/>
    <path d="M15 15h.01"/>
  </svg>
);

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const Features: React.FC = () => {
  const features: Feature[] = [
    {
      icon: <WhaleIcon size={32} />,
      title: 'Whale Tracking',
      description: 'Monitor large traders and their positions. See what the smart money is doing before everyone else.'
    },
    {
      icon: <Zap size={32} />,
      title: 'Flow Analysis',
      description: 'Advanced algorithms detect unusual patterns and market inefficiencies in real-time.'
    },
    {
      icon: <Target size={32} />,
      title: 'Custom Filters',
      description: 'Set up alerts based on volume thresholds and market categories.'
    }
  ];

  return (
    <section id="features" className="features">
      <div className="section-container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ 
            duration: 0.6,
            ease: [0.25, 0.25, 0.25, 0.75]
          }}
          viewport={{ 
            once: true, 
            margin: "-50px",
            amount: 0.3 
          }}
        >
          <h2>Intelligence That Moves Markets</h2>
          <p>Everything you need to stay ahead of the Polymarket flow</p>
        </motion.div>
        
        <div className="features-grid">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className="feature-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.5, 
                delay: index * 0.08,
                ease: [0.25, 0.25, 0.25, 0.75]
              }}
              viewport={{ 
                once: true, 
                margin: "-30px",
                amount: 0.2 
              }}
              whileHover={{ 
                y: -4,
                transition: { duration: 0.2, ease: "easeOut" }
              }}
            >
              <div className="feature-icon">{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features; 